import { sql } from 'drizzle-orm'
import { bigint, check, index, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId } from './columns.ts'
import { offeringVariant } from './offerings.ts'

/**
 * Which quality levels a performer is allowed to work at (spec §1 "Poziom",
 * §2 "Admin nadaje freelancerowi, które poziomy może obsługiwać").
 *
 * A grant, not a role. `access-control.ts` already says so in prose — "Skill
 * tier (CLEAN / DYNAMIC / ADVANCED) is NOT a role — it is an attribute of a
 * graphic/montage employee that gates which job categories they may be
 * assigned. It never enters this catalogue." This table is that sentence made
 * real; until now it was a comment describing something that did not exist.
 *
 * Modelling it as a role would have been the obvious shortcut and is wrong
 * twice over: the levels are per PRODUCT (a montażysta may do Pro reels and
 * only Starter films), and they are catalogue data an Admin retunes, while
 * roles are a fixed set the code branches on.
 *
 * Keyed on the VARIANT alone — the variant already names its offering, so
 * carrying an `offering_id` beside it would be a second copy of the same fact
 * and a second thing that can contradict the first. Reads that need the
 * offering join through the variant, which is one hop and cannot drift.
 */
export const userSkill = pgTable(
  'user_skill',
  {
    id: primaryId('uskl'),

    /**
     * The performer. `CASCADE`, unlike almost every other reference to `user`
     * in this schema: a skill grant is not a record of anything that happened,
     * it is a live permission, and a permission belonging to a deleted account
     * is only a way to assign work to nobody.
     */
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),

    /** The level they may take, e.g. Miniaturka · Creator. */
    variantId: text('variant_id')
      .notNull()
      .references(() => offeringVariant.id, { onDelete: 'cascade' }),

    /**
     * What THIS person is paid for this level, NET grosze — overriding the
     * variant's own rate (spec §17 · J1).
     *
     * NULL is the normal case and means "the catalogue's rate", not "nothing".
     * The override exists because a fixed rate is a negotiated number and two
     * people at the same level are not always worth the same to the business —
     * but it lives HERE, on the grant, rather than on the user: a rate is
     * per LEVEL, and a montażysta who does Pro reels and Starter films is paid
     * two different amounts.
     *
     * Read at ONE moment, when the line is paid for and the rate is snapshotted
     * onto it. Editing it afterwards changes what the next job pays and never
     * what an issued payout owes — same rule as the price list.
     */
    rateNetMinor: bigint('rate_net_minor', { mode: 'number' }),

    /**
     * Who granted it. `SET NULL` — the grant survives the granter leaving,
     * because the question it answers ("may this person take Pro work?") does
     * not stop mattering when the Admin who said yes moves on.
     */
    grantedByUserId: text('granted_by_user_id').references(() => user.id, {
      onDelete: 'set null',
    }),

    createdAt: createdAt(),
  },
  (t) => [
    // One grant per person per level. Without it, "revoke" would have to guess
    // how many rows to delete, and the capacity join would multiply a
    // performer's daily capacity by however many duplicates they had collected.
    uniqueIndex('user_skill_user_variant_unique').on(t.userId, t.variantId),
    // "Who can take this level?" — the read the auto-assignment does on every
    // paid line, joined against `capacity_day`.
    index('user_skill_variant_idx').on(t.variantId),
    // Same reasoning as the catalogue's own rate: zero is not a negotiated
    // number, it is an empty box somebody saved.
    check('user_skill_rate_positive', sql`${t.rateNetMinor} IS NULL OR ${t.rateNetMinor} > 0`),
  ],
)

export type UserSkill = typeof userSkill.$inferSelect
export type NewUserSkill = typeof userSkill.$inferInsert
